import { Formik, FormikHelpers, getIn } from "formik";
import { Button, Form, Modal } from "react-bootstrap";
import {
  organisationDetailsSchema,
  personalDetailsSchema,
  registerSchema,
} from "./schema";
import logoImage from "../../../assets/login/login-logo.png";
import styled from "styled-components";
import StepProgress from "./StepProgress";
import { useState } from "react";
import OrganisationDetailsForm from "./OrganisationDetailsForm";
import { RegisterFormModel } from "../../../types/Register/RegisterFormModel";
import PersonalDetailsForm from "./PersonalDetailsForm";
import {
  EndCol,
  FooterCol,
  FooterLayout,
} from "../../../@ui/ModalFooterLayout";
import { useNavigate } from "react-router-dom";
import { RegisterFormInitialValues } from "../../../types/Register/RegisterFormInitialValues";
import { RegisterRequest } from "../../../types/SsoRegisterRequest";
import useSsoService from "../../../hooks/useSsoService";
import { useTenantContext } from "../../../context/Tenant/TenantContext";

const HeaderMain = styled.div`
  font-size: 1.5rem;
`;

const HeaderSecondary = styled.div`
  font-size: 1rem;
  color: var(--bs-secondary-color, var(--bs-body-color));
`;

const Register = () => {
  const [step, setStep] = useState(0);
  const navigate = useNavigate();
  const ssoService = useSsoService();

  const handleSubmit = (values: RegisterFormModel, { setStatus }: FormikHelpers<RegisterFormModel>) => {
    ssoService
      .Register(mapToApiModel(values))
      .then(() => navigate("/login"))
      .catch((error) => {
        setStatus(error.response?.data?.message || error.message);
      });
  };

  const mapToApiModel = (values: RegisterFormModel) => {
    var { organisationDetails, personalDetails } = values;

    return {
      username: personalDetails.userName,
      email: personalDetails.email,
      password: personalDetails.password,
      firstname: personalDetails.firstName,
      lastname: personalDetails.secondName,
      organisationName: organisationDetails.name,
      organisationBillingEmail: organisationDetails.billingEmail,
      organisationPhone: organisationDetails.phone,
      organisationVatNumber: organisationDetails.vatNumber,
      organisationTransactionCurrencyCode: organisationDetails.currencyCode,
      address: {
        firstLine: organisationDetails.address.firstLine,
        secondLine: organisationDetails.address.secondLine,
        city: organisationDetails.address.city,
        state: organisationDetails.address.state,
        postcode: organisationDetails.address.postCode,
        countryId: organisationDetails.address.countryId,
      },
    } as RegisterRequest;
  };

  const steps = [
    {
      title: "Personal details",
      component: <PersonalDetailsForm />,
      schema: personalDetailsSchema,
      header: "Create an account",
      subHeader: "Register with your email address",
      field: "personalDetails",
    },
    {
      title: "Organisation details",
      component: <OrganisationDetailsForm />,
      schema: organisationDetailsSchema,
      header: "Organisation details",
      subHeader: "Tell us about your organisation",
      field: "organisationDetails",
    },
  ];

  const handleNextStep = async (
    step: number,
    setFieldTouched: any,
    errors: any,
  ) => {
    const nest = steps[step].field;

    const fieldList = Object.keys(steps[step].schema.fields);
    fieldList.forEach((field) =>
      setFieldTouched(`${nest}.${field}`, true, true),
    );

    const hasError = fieldList.some((property) =>
      getIn(errors[nest], property),
    );

    if (!hasError) {
      setStep(step + 1);
    }
  };

  const config = useTenantContext().config;

  return (
    <Formik
      validationSchema={registerSchema}
      initialValues={RegisterFormInitialValues}
      onSubmit={handleSubmit}
    >
      {({ handleSubmit, values, setFieldTouched, errors, status }) => (
        <Form onSubmit={handleSubmit}>
          <Modal.Header className="bg-secondary text-white">
            <Modal.Title>Register</Modal.Title>
            {config.name === "Inrix" ?
              <img
                style={{ height: "20px" }}
                src={config.mainLogo}
                alt="DataCutter"
              />
            : <img
                className="w-25 h-100"
                src={config.mainLogo}
                alt="DataCutter"
              />
            }
          </Modal.Header>
          <Modal.Body>
            <StepProgress totalSteps={steps.length} currentStep={step} />
            <div className="text-center">
              <HeaderMain className="text-primary">
                {steps[step].header}
              </HeaderMain>
              <HeaderSecondary>{steps[step].subHeader}</HeaderSecondary>
            </div>
            {steps[step].component}
            {status && (
              <div className="alert alert-danger mt-2" role="alert">
                {status}
              </div>
            )}
          </Modal.Body>
          <Modal.Footer>
            <FooterLayout>
              <FooterCol>
                <Button
                  variant="link"
                  onClick={() => {
                    navigate("/login");
                  }}
                >
                  Already have an account?
                </Button>
              </FooterCol>
              <EndCol>
                {step > 0 && (
                  <Button onClick={() => setStep(step - 1)}>Back</Button>
                )}

                {step < steps.length - 1 && (
                  <Button
                    onClick={() =>
                      handleNextStep(step, setFieldTouched, errors)
                    }
                  >
                    Next
                  </Button>
                )}
                {step === steps.length - 1 && (
                  <Button type="submit">Register</Button>
                )}
              </EndCol>
            </FooterLayout>
          </Modal.Footer>
        </Form>
      )}
    </Formik>
  );
};

export default Register;
